import { add, flatBasis, norm, scale, type Vec3 } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyStatus, coneTargets, explode, isEnemy, playersInRadius } from "../../sim/combat.ts";
import { constructsInRadius, damageConstruct, spawnConstruct } from "../../sim/constructs.ts";
import { FxKind } from "../../sim/events.ts";
import { targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, knockUp, launchProjectile, ownerAlive, pull, ready, simpleProjectile, trigger, useUlt } from "../kitlib.ts";

export const V = { combo: 0, wallCount: 1, armorPulse: 2 } as const;
export const PK = { SeedBomb: pk(HeroId.Root, 0) } as const;
export const CK = { ThornWall: ck(HeroId.Root, 0), IronWall: ck(HeroId.Root, 1) } as const;

const def: HeroDef = {
  id: HeroId.Root,
  slug: "root",
  name: "Root",
  role: Role.Tank,
  color: "#7a5a35",
  blurb: "Huge living-wood vanguard who grows solid cover and drags enemies into a bramble prison.",
  health: 850,
  armor: 0,
  shields: 0,
  radius: 0.72,
  height: 2.75,
  headRadius: 0.34,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 2800,
  abilities: {
    [Slot.Primary]: { name: "Branch Brawl", key: "LMB", description: "Swing heavy wooden arms for 65 damage in a three-hit combo; the third hit pops foes upward.", cooldown: 0 },
    [Slot.Secondary]: { name: "Pollen Bomb", key: "RMB", description: "Lob a seed pod that bursts in a 5m, 55 damage cloud.", cooldown: 0 },
    [Slot.Ability1]: { name: "Bramble Slab", key: "Shift", description: "Aim, then LMB to raise a thorny solid wall. Nearby enemies are scratched repeatedly.", cooldown: 6, charges: 2 },
    [Slot.Ability2]: { name: "Heartwood Wall", key: "E", description: "Aim, then LMB to raise a 600-HP wall that grants Root bonus health while he shelters by it.", cooldown: 12 },
    [Slot.Ultimate]: { name: "Tangleheart", key: "Q", description: "Throw a root mass that pulls, roots and damages enemies in its grip.", cooldown: 0 },
  },
  passive: { name: "Living Rampart", description: "Root's walls are solid, damageable cover that block movement and shots." },
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.combo, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      commonInput(sim, eid, input, pressed);
      setHv(c, eid, V.wallCount, constructsInRadius(sim, sim.posOf(eid), 24, (e) => c.Construct.owner[e] === eid).length);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        action(sim, eid, ActionKind.Ult, 35);
        const center = add(aim(sim, eid, 26, false).point, { x: 0, y: 0.8, z: 0 });
        sim.emit({ type: "explosion", pos: center, radius: 8, kind: FxKind.Spore });
        for (const { eid: t } of playersInRadius(sim, center, 8, (e) => isEnemy(sim, eid, e), false)) {
          pull(sim, t, center, { speed: 18, seconds: 0.65, source: eid });
          applyStatus(sim, t, "root", 3.5, { source: eid });
          applyDamage(sim, t, 70, eid, { pos: center });
        }
      }

      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 18);
        const p = targetPoint(sim, eid, Slot.Ability2);
        spawnConstruct(sim, eid, CK.IronWall, p.pos, p.yaw, { seconds: 12 });
      }

      if ((pressed & Btn.Ability1) && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        action(sim, eid, ActionKind.Cast, 14);
        const p = targetPoint(sim, eid, Slot.Ability1);
        spawnConstruct(sim, eid, CK.ThornWall, p.pos, p.yaw, { seconds: 8 });
      }

      if ((input.buttons & Btn.Secondary) && trigger(sim, eid, true, true)) {
        c.Weapon.fireCd[eid] = 42;
        action(sim, eid, ActionKind.AltFire, 16);
        launchProjectile(sim, eid, PK.SeedBomb, 60, input, { radius: 0.25, seconds: 1.6, gravity: 10 });
      }

      if ((input.buttons & Btn.Primary) && c.Weapon.fireCd[eid]! <= 0) {
        c.Weapon.fireCd[eid] = 42;
        c.Weapon.shots[eid] = (c.Weapon.shots[eid]! + 1) & 0xffff;
        action(sim, eid, ActionKind.Melee, 18);
        const combo = (hv(c, eid, V.combo) % 3) + 1;
        setHv(c, eid, V.combo, combo);
        for (const t of coneTargets(sim, eid, 4.3, 0.85, (e) => isEnemy(sim, eid, e))) {
          applyDamage(sim, t, 65, eid);
          if (combo === 3) knockUp(sim, t, 5.5);
        }
        for (const { eid: w } of constructsInRadius(sim, aim(sim, eid, 4, false).point, 1.1, (e) => c.Construct.team[e] !== c.Player.team[eid])) damageConstruct(sim, w, 65, eid);
      }
    },
  },
  targets: {
    [Slot.Ability1]: { range: 18, clearance: 0.8, construct: CK.ThornWall, verb: "Place" },
    [Slot.Ability2]: { range: 10, clearance: 0.8, construct: CK.IronWall, verb: "Place" },
  },
  projectiles: {
    [PK.SeedBomb]: simpleProjectile(
      (sim, p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        explode(sim, owner, hit.pos, { radius: 5, damage: 55, minDamage: 55, knockback: 4, fxKind: FxKind.Spore, skip: p });
      },
      { color: "#b7ff68", size: 0.26, trail: true },
    ),
  },
  constructs: {
    [CK.ThornWall]: {
      maxHp: 250,
      seconds: 8,
      shape: { kind: "box", hw: 2.3, hd: 0.28, h: 3.0 },
      solid: true,
      blocksShots: true,
      damageable: true,
      friendlyDamage: true,
      visual: { color: "#8abf48", shape: "box" },
      tick(sim, e) {
        const owner = sim.c.Construct.owner[e]!;
        if (!ownerAlive(sim, owner) || sim.tick % 30 !== 0) return;
        const p = add(sim.posOf(e), { x: 0, y: 1.3, z: 0 });
        for (const { eid: t } of playersInRadius(sim, p, 4, (x) => isEnemy(sim, owner, x), false)) applyDamage(sim, t, 60, owner, { pos: p });
      },
    },
    [CK.IronWall]: {
      maxHp: 600,
      seconds: 12,
      shape: { kind: "box", hw: 2.7, hd: 0.34, h: 3.4 },
      solid: true,
      blocksShots: true,
      damageable: true,
      friendlyDamage: true,
      visual: { color: "#5f3e26", shape: "box" },
      tick(sim, e) {
        const owner = sim.c.Construct.owner[e]!;
        if (!ownerAlive(sim, owner)) return;
        const p = sim.posOf(e);
        if (Math.hypot(sim.c.Pos.x[owner]! - p.x, sim.c.Pos.z[owner]! - p.z) < 15) {
          addOverhealth(sim, owner, 50, 20, owner);
          setHv(sim.c, owner, V.armorPulse, 1);
        } else setHv(sim.c, owner, V.armorPulse, 0);
      },
    },
  },
};

export function rootWallForward(yaw: number): Vec3 {
  const { fwd } = flatBasis(yaw);
  return norm(scale(fwd, 1));
}

export default module;
